import { computed, onBeforeUnmount, onMounted, reactive, ref, watch } from 'vue';
import { getCmsContent, searchCms, searchProducts } from '../api/searchApi';
import type { KeywordSearchResponse } from '../models/ApiModels';
import type { DisciplineFacet, PrimaryFacetGroup, ProductFacet, SeriesFacet } from '../models/FacetModels';
import type { Product, UserList } from '../models/ProductModels';
import type { CmsContent, InformationResult, ResultType, SearchAppConfig, SearchState, SortOrder } from '../models/SearchModels';
import { setBodyScrollLocked } from '../utils/bodyScroll';
import { clearFacetGroup, filterFacetsForDivision, groupPrimaryFacets, sortPrimarySeries, toggleFacetSelection } from '../utils/facetUtils';
import { highlightDivision, renderCmsHeader, showModal, trackProducts, trackRoute } from '../utils/legacyInterop';
import { pageRange, shouldRedirectSingleProduct } from '../utils/productUtils';
import { buildSearchUrl, parseSearchUrl, updateSearchUrl } from '../utils/searchUrl';

const emptyCms: CmsContent = { ConfigContent: '', HeaderContent: '', Background: '' };

function normalizeDivision(division: string): string {
  const value = division.toLowerCase();
  return value === 'vocational' || value === 'tafe-rto' ? 'vet' : value;
}

function publishedYearLabels(facets: ProductFacet[]): ProductFacet[] {
  const year = new Date().getFullYear();
  return facets.map((facet) => ({
    ...facet,
    Facet: facet.Facet.map((value) => {
      const label = value.AttributeTypeValue.toLowerCase();
      if (facet.AttributeType.toLowerCase() !== 'published year') return value;
      if (label === 'last 1 years') return { ...value, AttributeTypeValue: `${year - 1} - ${year}` };
      if (label === 'last 3 years') return { ...value, AttributeTypeValue: `${year - 3} - ${year - 2}` };
      if (label === 'last 5 years') return { ...value, AttributeTypeValue: `${year - 5} - ${year - 4}` };
      return value;
    })
  }));
}

export function useSearch(config: SearchAppConfig) {
  const state = reactive<SearchState>(parseSearchUrl());
  const products = ref<Product[]>([]);
  const productCount = ref(0);
  const productsPerPage = ref(20);
  const informationResults = ref<InformationResult[]>([]);
  const informationCount = ref(0);
  const productFacets = ref<ProductFacet[]>([]);
  const seriesFacets = ref<SeriesFacet[]>([]);
  const disciplineFacets = ref<DisciplineFacet[]>([]);
  const primaryFacetGroups = ref<PrimaryFacetGroup[]>([]);
  const userLists = ref<UserList[]>([]);
  const userSk = ref(0);
  const countryCode = ref('AU');
  const divisionCounts = reactive({ primary: 0, secondary: 0, university: 0, vet: 0 });
  const cmsContent = ref<CmsContent>({ ...emptyCms });
  const isLoading = ref(false);
  const isCheckoutLoading = ref(false);
  const searchError = ref<string | null>(null);
  const mobileFiltersVisible = ref(false);
  const mobileSortVisible = ref(false);
  const dismissedCatalogueWarning = ref(false);
  let searchController: AbortController | null = null;
  let cmsController: AbortController | null = null;

  const totalResults = computed(() => state.resultType === 'P' ? productCount.value : informationCount.value);
  const totalPages = computed(() => Math.max(1, Math.ceil(totalResults.value / (state.resultType === 'P' ? productsPerPage.value : 20))));
  const visiblePages = computed(() => pageRange(state.page, totalPages.value));
  const hasFilters = computed(() => Boolean(state.query || state.facetSelected || state.categorySk || state.disciplineSk || state.seriesSk || state.showFacet));
  const hasRefinements = computed(() => Boolean(state.facetSelected || state.categorySk || state.disciplineSk || state.seriesSk || state.showFacet));
  const nonSalableCount = computed(() => products.value.filter((product) => product.AllowSale === 'N').length);
  const startResult = computed(() => totalResults.value ? ((state.page - 1) * (state.resultType === 'P' ? productsPerPage.value : 20)) + 1 : 0);
  const endResult = computed(() => Math.min(totalResults.value, startResult.value + (state.resultType === 'P' ? products.value.length : informationResults.value.length) - 1));

  function applyProductResponse(response: KeywordSearchResponse): void {
    products.value = response.Products ?? [];
    productCount.value = Number(response.ProductsCount) || 0;
    productsPerPage.value = Number(response.ProductsPerPage) || 20;
    userSk.value = Number(response.UserSK) || 0;
    countryCode.value = response.CountryCode || 'AU';
    userLists.value = response.UserLists ?? [];
    divisionCounts.primary = Number(response.PrimaryProductsCount) || 0;
    divisionCounts.secondary = Number(response.SecondaryProductsCount) || 0;
    divisionCounts.university = Number(response.HEProductsCount) || 0;
    divisionCounts.vet = Number(response.TAFEProductsCount) || 0;
    const facets = publishedYearLabels(response.ProductFacets ?? []);
    productFacets.value = filterFacetsForDivision(facets, state.division, countryCode.value);
    seriesFacets.value = sortPrimarySeries(response.SeriesFacet ?? []);
    disciplineFacets.value = response.DisiplineFacets ?? [];
    primaryFacetGroups.value = state.division.toLowerCase() === 'primary' ? groupPrimaryFacets(productFacets.value) : [];

    if (shouldRedirectSingleProduct(products.value, productCount.value, state)) {
      window.location.assign(products.value[0].ProductURL);
      return;
    }
    if (productCount.value === 1 && state.division === 'all' && products.value[0]?.IsEdition === 'Y') {
      const autoCountryPopup = (document.getElementById('autocountrypopup') as HTMLInputElement | null)?.value;
      if (autoCountryPopup !== 'true') showModal('#AllDivisionModal');
      else {
        const edition = document.getElementById('NewSearchEdition') as HTMLInputElement | null;
        if (edition) edition.value = 'Available';
      }
    }
    trackProducts(products.value);
  }

  async function executeSearch(): Promise<void> {
    searchController?.abort();
    cmsController?.abort();
    searchController = new AbortController();
    cmsController = new AbortController();
    const activeSearchController = searchController;
    const activeCmsController = cmsController;
    isLoading.value = true;
    searchError.value = null;
    state.division = normalizeDivision(state.division);
    highlightDivision(state.division);

    try {
      const searchPromise = state.resultType === 'P'
        ? searchProducts(state, activeSearchController.signal).then(applyProductResponse)
        : searchCms(state, activeSearchController.signal).then((response) => {
            informationResults.value = response.Tabs ?? response.CMSResult ?? [];
            informationCount.value = Number(response.TotalPages ?? response.TotalCount) || 0;
          });
      const contentPromise = getCmsContent(state, activeCmsController.signal).then((content) => {
        cmsContent.value = content;
        renderCmsHeader(content.HeaderContent, content.Background);
      });
      await Promise.all([searchPromise, contentPromise]);
    } catch (error) {
      if (error instanceof DOMException && error.name === 'AbortError') return;
      searchError.value = 'Sorry, the search function is not working currently. Please try again soon.';
      console.error('Search request failed.', error);
    } finally {
      if (!activeSearchController.signal.aborted) isLoading.value = false;
    }
  }

  function commit(partial: Partial<SearchState>, replace = false): void {
    Object.assign(state, partial);
    updateSearchUrl(state, replace);
    trackRoute(buildSearchUrl(state));
    void executeSearch();
  }

  function setQuery(query: string): void { commit({ query: query.trim(), page: 1, resultType: 'P' }); }
  function setSortOrder(sortOrder: SortOrder): void { commit({ sortOrder, page: 1 }); }
  function setPage(page: number): void {
    if (page < 1 || page > totalPages.value || page === state.page) return;
    commit({ page });
    window.scrollTo({ top: 0, behavior: 'smooth' });
  }
  function selectDivision(division: string): void { commit({ division, page: 1, facetSelected: '', categorySk: 0, disciplineSk: 0, seriesSk: 0, disciplineType: '', showFacet: '' }); }
  function setResultType(resultType: ResultType): void { commit({ resultType, page: 1 }, true); }
  function selectFacet(attributeSk: number, valueSk: number): void { commit({ facetSelected: toggleFacetSelection(state.facetSelected, attributeSk, valueSk), page: 1 }); }
  function clearFacet(attributeSk: number): void { commit({ facetSelected: clearFacetGroup(state.facetSelected, attributeSk), page: 1 }); }
  function selectSeries(attributeSk: number, valueSk: number): void { selectFacet(attributeSk, valueSk); }
  function selectCategory(categorySk: number): void { commit({ categorySk, disciplineSk: 0, seriesSk: 0, disciplineType: 'C', page: 1 }); }
  function selectDiscipline(categorySk: number, disciplineSk: number): void { commit({ categorySk, disciplineSk, seriesSk: 0, disciplineType: 'CD', page: 1 }); }
  function selectSubject(categorySk: number, disciplineSk: number, seriesSk: number): void { commit({ categorySk, disciplineSk, seriesSk, disciplineType: 'CDS', page: 1 }); }
  function clearAll(): void { commit({ query: '', facetSelected: '', categorySk: 0, disciplineSk: 0, seriesSk: 0, disciplineType: '', showFacet: '', page: 1 }); }
  function clearQuery(): void { commit({ query: '', page: 1 }); }
  function toggleShowOnly(type: string): void {
    const selected = new Set(state.showFacet.split(',').filter(Boolean).map((value) => value.toLowerCase()));
    const key = type.toLowerCase();
    selected.has(key) ? selected.delete(key) : selected.add(key);
    commit({ showFacet: [...selected].join(','), page: 1 });
  }

  function handlePopState(): void {
    Object.assign(state, parseSearchUrl());
    void executeSearch();
  }

  watch(isLoading, setBodyScrollLocked);
  onMounted(() => {
    window.addEventListener('popstate', handlePopState);
    window.CengageSearchApp = { search: setQuery };
    if (`${window.location.pathname}${window.location.search}` !== buildSearchUrl(state)) updateSearchUrl(state, true);
    void executeSearch();
  });
  onBeforeUnmount(() => {
    searchController?.abort();
    cmsController?.abort();
    window.removeEventListener('popstate', handlePopState);
    delete window.CengageSearchApp;
    setBodyScrollLocked(false);
  });

  return {
    config, state, products, productCount, productsPerPage, informationResults, informationCount,
    productFacets, seriesFacets, disciplineFacets, primaryFacetGroups, userLists, userSk, countryCode,
    divisionCounts, cmsContent, isLoading, isCheckoutLoading, searchError, mobileFiltersVisible,
    mobileSortVisible, dismissedCatalogueWarning, totalResults, totalPages, visiblePages,
    hasFilters, hasRefinements, nonSalableCount, startResult, endResult, executeSearch, setQuery, setSortOrder, setPage,
    selectDivision, setResultType, selectFacet, clearFacet, selectSeries, selectCategory, selectDiscipline,
    selectSubject, clearAll, clearQuery, toggleShowOnly
  };
}

export type SearchController = ReturnType<typeof useSearch>;
